import { LanguageCode, SUPPORTED_LANGUAGES } from '@/app/store/slices/languageSlice';

// Local storage key for guest user language preference
const GUEST_LANGUAGE_KEY = 'smartmatrix_guest_language';

/**
 * Detect browser language and return supported language code
 * Falls back to 'en-US' if browser language is not supported
 */
export const detectBrowserLanguage = (): LanguageCode => {
  if (typeof window === 'undefined') {
    return 'en-US';
  }

  // Get browser language (e.g., 'en-US', 'zh-CN', 'ja')
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  // Try exact match first (e.g., "en-US" -> "en-US")
  if (browserLang in SUPPORTED_LANGUAGES) {
    return browserLang as LanguageCode;
  }
  
  // Extract the primary language code and map to default region
  const primaryLang = browserLang.split('-')[0].toLowerCase();
  
  // Map primary language codes to BCP 47 format with default regions
  const languageMap: Record<string, LanguageCode> = {
    'en': 'en-US',
    'zh': 'zh-CN',  // Default to Simplified Chinese
    'ja': 'ja-JP',
    'es': 'es-ES',
    'fr': 'fr-FR',
  };
  
  // Return mapped language or fallback to English (United States)
  return languageMap[primaryLang] || 'en-US';
};

/**
 * Check if a language code is RTL (Right-to-Left)
 */
export const isRTL = (languageCode: string): boolean => {
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  return rtlLanguages.includes(languageCode);
};

/**
 * Update HTML lang and dir attributes
 */
export const updateHtmlAttributes = (languageCode: LanguageCode): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const html = document.documentElement;
  
  // Update lang attribute
  html.setAttribute('lang', languageCode);
  
  // Update dir attribute for RTL languages
  html.setAttribute('dir', isRTL(languageCode) ? 'rtl' : 'ltr');
};

/**
 * Save language preference to local storage (for guest users)
 */
export const saveGuestLanguagePreference = (languageCode: LanguageCode): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(GUEST_LANGUAGE_KEY, languageCode);
  } catch (error) {
    console.error('Failed to save guest language preference:', error);
  }
};

/**
 * Load language preference from local storage (for guest users)
 */
export const loadGuestLanguagePreference = (): LanguageCode | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const savedLanguage = localStorage.getItem(GUEST_LANGUAGE_KEY);
    
    // Validate that the saved language is supported
    if (savedLanguage && savedLanguage in SUPPORTED_LANGUAGES) {
      return savedLanguage as LanguageCode;
    }
    
    return null;
  } catch (error) {
    console.error('Failed to load guest language preference:', error);
    return null;
  }
};

/**
 * Clear guest language preference from local storage
 * Called after migrating to database on login
 */
export const clearGuestLanguagePreference = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(GUEST_LANGUAGE_KEY);
  } catch (error) {
    console.error('Failed to clear guest language preference:', error);
  }
};

/**
 * Validate language code
 */
export const isValidLanguageCode = (code: string): code is LanguageCode => {
  return code in SUPPORTED_LANGUAGES;
};
